import { useState } from 'react'
import {
  PageHeader,
  Button,
  Card,
  LoadingState,
  ErrorState,
  EmptyState,
  DataTable,
  Modal,
  Input,
  Textarea,
  Select,
  Switch,
  useToast,
  ConfirmDialog,
} from '@/components/ui'
import { Mail, Plus, Pencil, Trash2 } from 'lucide-react'
import axios from 'axios'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'

interface EmailTemplate {
  id: string
  tenantId: string
  name: string
  subject: string
  body: string
  templateType: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

const TEMPLATE_TYPES = [
  { value: 'ausseneinsatz', label: 'Ausseneinsatz' },
  { value: 'general', label: 'Allgemein' },
]

const PLACEHOLDER_HINTS = [
  '{{customerName}}',
  '{{technicianName}}',
  '{{scheduledDate}}',
  '{{scheduledTime}}',
  '{{location}}',
  '{{topic}}',
  '{{note}}',
]

async function fetchTemplates(): Promise<EmailTemplate[]> {
  const res = await axios.get<{ data: EmailTemplate[] }>('/api/dispo/email-templates')
  return res.data.data
}

interface FormState {
  name: string
  subject: string
  body: string
  templateType: string
  isActive: boolean
}

function defaultForm(): FormState {
  return {
    name: '',
    subject: 'Ausseneinsatz-Termin am {{scheduledDate}}',
    body: `Sehr geehrte Damen und Herren,

wir moechten Ihnen folgenden Ausseneinsatz-Termin bestaetigen:

Datum: {{scheduledDate}}
Uhrzeit: {{scheduledTime}}
Thema: {{topic}}
Techniker: {{technicianName}}
Ort: {{location}}

Bei Rueckfragen stehen wir Ihnen gerne zur Verfuegung.

Mit freundlichen Gruessen`,
    templateType: 'ausseneinsatz',
    isActive: true,
  }
}

export function DispoEmailVorlagenPage() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [formOpen, setFormOpen] = useState(false)
  const [editTemplate, setEditTemplate] = useState<EmailTemplate | null>(null)
  const [form, setForm] = useState<FormState>(defaultForm())
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null)

  const {
    data: templates,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['dispo-email-templates'],
    queryFn: fetchTemplates,
    staleTime: 30_000,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editTemplate) {
        return axios.patch(`/api/dispo/email-templates/${editTemplate.id}`, form)
      }
      return axios.post('/api/dispo/email-templates', form)
    },
    onSuccess: () => {
      toast({ title: editTemplate ? 'Vorlage gespeichert' : 'Vorlage erstellt', tone: 'success' })
      queryClient.invalidateQueries({ queryKey: ['dispo-email-templates'] })
      setFormOpen(false)
      setEditTemplate(null)
    },
    onError: () => {
      toast({ title: 'Fehler beim Speichern', tone: 'error' })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => axios.delete(`/api/dispo/email-templates/${id}`),
    onSuccess: () => {
      toast({ title: 'Vorlage geloescht', tone: 'success' })
      queryClient.invalidateQueries({ queryKey: ['dispo-email-templates'] })
    },
    onError: () => {
      toast({ title: 'Fehler beim Loeschen', tone: 'error' })
    },
  })

  function openCreate() {
    setEditTemplate(null)
    setForm(defaultForm())
    setFormOpen(true)
  }

  function openEdit(t: EmailTemplate) {
    setEditTemplate(t)
    setForm({
      name: t.name,
      subject: t.subject,
      body: t.body,
      templateType: t.templateType,
      isActive: t.isActive,
    })
    setFormOpen(true)
  }

  const columns: ColumnDef<EmailTemplate>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'subject',
      header: 'Betreff',
      cell: ({ row }) => (
        <span className="text-sm text-gray-600 truncate max-w-xs block">{row.original.subject}</span>
      ),
    },
    {
      accessorKey: 'templateType',
      header: 'Typ',
      cell: ({ row }) => (
        <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
          {TEMPLATE_TYPES.find((t) => t.value === row.original.templateType)?.label ??
            row.original.templateType}
        </span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Aktiv',
      cell: ({ row }) => (
        <span className={`text-xs font-medium ${row.original.isActive ? 'text-green-600' : 'text-gray-400'}`}>
          {row.original.isActive ? 'Ja' : 'Nein'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-2 justify-end">
          <Button size="xs" variant="ghost" onClick={() => openEdit(row.original)}>
            <Pencil className="w-3.5 h-3.5" />
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setDeleteTarget(row.original)}>
            <Trash2 className="w-3.5 h-3.5 text-red-500" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="E-Mail-Vorlagen"
        actions={
          <Button variant="primary" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1.5" />
            Neue Vorlage
          </Button>
        }
      />

      {isLoading && <LoadingState message="Vorlagen werden geladen..." />}
      {error && <ErrorState message="Fehler beim Laden der Vorlagen" details={String(error)} />}

      {!isLoading && !error && (
        <Card>
          {!templates?.length ? (
            <EmptyState
              icon={<Mail className="w-8 h-8" />}
              title="Keine E-Mail-Vorlagen vorhanden"
              description="Erstellen Sie eine Vorlage fuer Ausseneinsatz-Terminbenachrichtigungen."
              action={
                <Button variant="primary" onClick={openCreate}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  Erste Vorlage erstellen
                </Button>
              }
            />
          ) : (
            <DataTable columns={columns} data={templates} />
          )}
        </Card>
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setFormOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              onClick={() => saveMutation.mutate()}
              disabled={
                saveMutation.isPending || !form.name.trim() || !form.subject.trim() || !form.body.trim()
              }
            >
              {saveMutation.isPending ? 'Speichern...' : 'Speichern'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="z.B. Standard-Ausseneinsatz"
          />
          <Select
            label="Typ"
            value={form.templateType}
            onValueChange={(v) => setForm((f) => ({ ...f, templateType: v }))}
            options={TEMPLATE_TYPES}
          />
          <Input
            label="Betreff"
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            placeholder="Betreff der E-Mail"
          />
          <div>
            <Textarea
              label="E-Mail-Text"
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={10}
              placeholder="Text der E-Mail..."
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Verfuegbare Platzhalter:{' '}
              {PLACEHOLDER_HINTS.map((ph) => (
                <code key={ph} className="bg-gray-100 px-1 py-0.5 rounded text-[11px] mr-1">
                  {ph}
                </code>
              ))}
            </p>
          </div>
          <Switch
            label="Aktiv"
            hint="Inaktive Vorlagen sind nicht bei der Einsatz-Erstellung auswahlbar"
            checked={form.isActive}
            onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id)
          setDeleteTarget(null)
        }}
        title="Vorlage loeschen"
        description={`Moechten Sie die Vorlage "${deleteTarget?.name}" wirklich loeschen? Bereits zugeordnete Ausseneinsaetze verlieren die Template-Referenz.`}
        confirmLabel="Loeschen"
        variant="danger"
      />
    </div>
  )
}
